import { historicalDetailError } from './detail-resources.js';
// Users 分析（Phase 2 v2）
//
// v2 在 v1 基础上加上了：
//   - 状态过滤胶囊（全部 / 活跃 / 沉睡 / 流失）
//   - 服务端排序：calls / totalSpent / lastSeen 列头可点
//   - 调用方代号搜索框
//   - 服务端分页；窗口汇总独立于当前页，数据变化时重新开始翻页

import React, { useEffect, useMemo, useState, useRef } from 'react';
import { XShell } from '../../shell.jsx';
import { XApi } from '../../services.js';
import { exactAnalyticsBounds } from './analytics-window.js';
import {
  KpiCard,
  KpiGrid,
  RangePicker,
  Section,
  Sparkline,
  EmptyTable,
  LoadingHint,
  FilterBar,
  PillGroup,
  SearchInput,
  downloadCsv,
  SortHeader,
  DataTable,
  fmtNum,
  fmtUsd,
  fmtRelDate,
} from './analytics-shared.jsx';

const STATUS_COLORS = {
  active: '#22a06b',
  dormant: '#e08e0b',
  churned: '#c0392b',
};
const STATUS_LABELS = {
  active: '活跃',
  dormant: '沉睡',
  churned: '流失',
};
const STATUS_OPTIONS = [
  { v: null, l: '全部' },
  { v: 'active', l: '活跃' },
  { v: 'dormant', l: '沉睡' },
  { v: 'churned', l: '流失' },
];
const PAGE_SIZE = 25;

export const UsersSection = ({ api }) => {
  const { authed, user } = XShell.useAuth();
  const owner = user?._raw?.id ?? user?.id ?? null;
  return <UsersState key={JSON.stringify([Boolean(authed), owner, api?.id])} api={authed ? api : null} />;
};

const UsersState = ({ api }) => {
  const { navigate } = XShell.useRoute();
  const apiId = api?.id;
  const [range, setRange] = useState('30d');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [insights, setInsights] = useState(null);
  const [activeWindows, setActiveWindows] = useState(null);
  const [activeLoading, setActiveLoading] = useState(false);
  const [activeError, setActiveError] = useState(false);
  const [activeReload, setActiveReload] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'calls', dir: 'desc' });
  const [pageState, setPageState] = useState({ key: '', cursor: null, previous: [] });
  const [searchTerm, setSearchTerm] = useState('');
  useEffect(() => { const timer = setTimeout(() => setSearchTerm(search.trim()), 250); return () => clearTimeout(timer); }, [search]);

  const bounds = useMemo(() => exactAnalyticsBounds(range, customRange), [range, customRange.from, customRange.to]);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const [exporting, setExporting] = useState(false);
  const queryKey = JSON.stringify([apiId, bounds, statusFilter, searchTerm, sort]);
  const mounted = useRef(true), exportQuery = useRef(queryKey), exportFlight = useRef(null);
  exportQuery.current = queryKey;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const currentPage = pageState.key === queryKey ? pageState : { key: queryKey, cursor: null, previous: [] };
  const cursor = currentPage.cursor;
  useEffect(() => {
    setPageState({ key: queryKey, cursor: null, previous: [] });
    exportFlight.current = null;
    setExporting(false);
  }, [queryKey]);
  useEffect(() => {
    let cancelled = false, restarting = false;
    setInsights(null); setError(null);
    if (!apiId || !bounds) {
      setLoading(false);
      if (apiId) setError('日期范围必须完整、正序且不超过 365 天（UTC）');
      return;
    }
    setLoading(true);
    XApi.providerDashboardService.callerPage({ ...bounds, apiId, cursor, search: searchTerm, status: statusFilter, sort: sort.key, dir: sort.dir, limit: PAGE_SIZE }).catch(async e => {
      if (e?.status !== 409 || cancelled || !cursor) throw e;
      restarting = true;
      setPageState({ key: queryKey, cursor: null, previous: [] });
      // The changed cursor starts the sole recovery request in this effect.
      return null;
    }).then((ins) => {
      if (cancelled || restarting) return;
      if (!ins) throw Error('unavailable');
      setInsights(ins);
    }).catch(e => { if (!cancelled) setError(historicalDetailError(e) ?? '调用方数据加载失败'); })
      .finally(() => { if (!cancelled && !restarting) setLoading(false); });
    return () => { cancelled = true; };
  }, [apiId, queryKey, cursor, reload]);

  // Rolling windows are auxiliary and independent of the selected date range.
  // A missing or failed endpoint must not discard the successfully loaded ledger.
  useEffect(() => {
    let cancelled = false;
    setActiveWindows(null); setActiveError(false);
    if (!apiId) { setActiveLoading(false); return; }
    setActiveLoading(true);
    XApi.providerDashboardService.activeCallersWindows({ apiId }).then((aw) => {
      if (cancelled) return;
      setActiveWindows(aw);
      setActiveError(!aw || aw.ready === false);
    }).catch(() => { if (!cancelled) setActiveError(true); })
      .finally(() => { if (!cancelled) setActiveLoading(false); });
    return () => { cancelled = true; };
  }, [apiId, activeReload]);

  const allCallers = insights?.items || [];
  const counts = insights?.summary?.counts || { active: 0, dormant: 0, churned: 0 };
  const totalSpent = insights?.summary?.totalGross ?? 0;
  const totalCallers = insights?.summary?.totalCallers ?? 0;
  const visible = allCallers;
  const hasMore = Boolean(insights?.nextCursor);
  const onSort = (key) => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 'asc' ? 'desc' : 'asc') : 'desc' }));
  const resetFilters = () => { setStatusFilter(null); setSearch(''); };
  const hasFilters = statusFilter || search.trim();

  if (!apiId) return null;
  return (
    <div style={{ padding: '24px 32px 64px' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 16, gap: 12, flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: 'var(--fg-0)', letterSpacing: '-0.01em' }}>
            Users
          </h2>
          <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4 }}>
            收益调用方榜单 · 次数为收益记录（不含无收益调用）· 金额为毛收入 · 按当前 Key 归属
          </div>
        </div>
        <RangePicker
          value={range}
          onChange={setRange}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
        />
      </div>

      {error && <div role="alert">{error} {bounds && <button className="btn btn-sm btn-ghost" onClick={() => setReload(n => n + 1)}>重试</button>}</div>}
      <div style={{fontSize:11,color:'var(--fg-3)',marginBottom:10}}>榜单仅包含仍关联 Key 的收益记录；状态按所选窗口内最近记录距今计算，非历史时点快照。</div>
      {activeError && <div role="status">滚动活跃调用方暂不可用 <button className="btn btn-sm btn-ghost" onClick={() => setActiveReload(n => n + 1)}>重试活跃窗口</button></div>}
      {/* A5 — 滚动活跃窗口：1d / 7d / 30d distinct，hourly cron 维护
          注意它跟"状态"分段不同：状态是基于 lastSeen 的分段（不可加和），
          这里是窗口内 distinct count（业务语义更准） */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 10,
          marginBottom: 14,
          padding: '12px 14px',
          background: 'var(--bg-1)',
          border: '1px solid var(--bd-1)',
          borderRadius: 8,
        }}
      >
        <div style={{ fontSize: 11, color: 'var(--fg-3)', gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between' }}>
          <span>滚动活跃调用方（定时更新，不随所选日期变化）</span>
          <span className="mono">
            {activeWindows?.updatedAt ? `更新于 ${new Date(activeWindows.updatedAt).toLocaleTimeString()}` : ''}
          </span>
        </div>
        {[
          { label: 'UTC 今日', key: '1d' },
          { label: '近 7 天', key: '7d' },
          { label: '近 30 天', key: '30d' },
        ].map((w) => {
          const v = activeWindows?.windows?.[w.key] ?? 0;
          return (
            <div key={w.key}>
              <div style={{ fontSize: 10, color: 'var(--fg-3)' }}>{w.label}</div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 500,
                  color: v > 0 ? 'var(--fg-0)' : 'var(--fg-3)',
                  lineHeight: 1.1,
                  marginTop: 2,
                }}
                className="mono"
              >
                {activeLoading ? '…' : activeWindows && activeWindows.ready !== false ? fmtNum(v) : '—'}
              </div>
            </div>
          );
        })}
      </div>

      <KpiGrid>
        <KpiCard label="调用方总数" value={loading ? '…' : insights ? fmtNum(totalCallers) : '—'} dim={!loading && totalCallers === 0} />
        <KpiCard label="活跃 (≤14天)" value={loading ? '…' : insights ? fmtNum(counts.active) : '—'} accent={STATUS_COLORS.active} dim={!loading && counts.active === 0} />
        <KpiCard label="沉睡 (14-30天)" value={loading ? '…' : insights ? fmtNum(counts.dormant) : '—'} accent={STATUS_COLORS.dormant} dim={!loading && counts.dormant === 0} />
        <KpiCard label="流失 (>30天)" value={loading ? '…' : insights ? fmtNum(counts.churned) : '—'} accent={STATUS_COLORS.churned} dim={!loading && counts.churned === 0} />
        <KpiCard label="榜单毛收入" value={loading ? '…' : insights ? fmtUsd(totalSpent) : '—'} dim={!loading && totalSpent === 0} />
      </KpiGrid>

      <Section
        title={error ? '调用方榜单（暂不可用）' : `调用方榜单 (${insights?.totalFiltered ?? 0} / ${totalCallers})`}
        action={
          <button className="btn btn-sm btn-ghost" disabled={exporting || loading || !insights?.totalFiltered} onClick={async () => {
            const flight = { queryKey };
            exportFlight.current = flight;
            const current = () => mounted.current && exportQuery.current === queryKey && exportFlight.current === flight;
            setExporting(true); setError(null);
            try {
              const result = await XApi.providerDashboardService.callerPage({ apiId, ...bounds, view: 'export', search: searchTerm, status: statusFilter, sort: sort.key, dir: sort.dir });
              if (!current()) return;
              if (result.nextCursor || result.items.length !== result.totalFiltered) throw Error('Incomplete export');
              const rows = result.items;
              if (!current()) return;
              downloadCsv(rows, [
              { key: 'callerRef', label: 'Caller ref' },
              { key: 'callerHandle', label: 'Caller' },
              { key: 'status', label: 'Status' },
              { key: 'earningRecords', label: 'Earning records' },
              { key: 'totalSpent', label: 'Gross USD' },
              { key: 'firstSeen', label: 'First seen', transform: (v) => v ? new Date(v).toISOString() : '' },
              { key: 'lastSeen', label: 'Last seen', transform: (v) => v ? new Date(v).toISOString() : '' },
            ], `callers-${api?.name || 'api'}-${new Date().toISOString().slice(0, 10)}.csv`);
            } catch (e) { if (current()) { if (historicalDetailError(e)) setInsights(null); setError(historicalDetailError(e) ?? (e?.status === 409 ? '导出期间数据已变化，请重新导出' : '导出失败，请重试')); } }
            finally { if (current()) { exportFlight.current = null; setExporting(false); } }
          }}>{exporting ? '正在导出…' : '导出筛选结果 CSV'}</button>
        }
      >
        <FilterBar>
          <PillGroup
            label="状态"
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={setStatusFilter}
          />
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="按调用方代号搜索"
            width={240}
          />
          {hasFilters && (
            <button
              type="button"
              onClick={resetFilters}
              style={{
                padding: '5px 10px', border: '1px solid var(--bd-1)',
                background: 'transparent', borderRadius: 6,
                color: 'var(--fg-3)', fontSize: 11, cursor: 'pointer',
              }}
            >清除筛选 ×</button>
          )}
        </FilterBar>

        {loading ? (
          <LoadingHint />
        ) : !insights ? <div role="status">调用方数据暂不可用</div> : totalCallers === 0 ? (
          <EmptyTable cols={7} rows={4} hint="所选日期内没有收益调用方" subHint="收益记录口径；免费或失败调用可能没有收益记录" />
        ) : visible.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-3)', fontSize: 12 }}>
            当前筛选下没有匹配的调用方 ·{' '}
            <button className="btn btn-sm btn-ghost" type="button" onClick={resetFilters} style={{ background: 'none', border: 'none', color: 'var(--fg-1)', textDecoration: 'underline', cursor: 'pointer', fontSize: 12, padding: 0 }}>清除筛选</button>
          </div>
        ) : (
          <>
            <DataTable>
              <thead>
                <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
                  <th style={{ padding: '6px 8px' }}>调用方</th>
                  <th style={{ padding: '6px 8px' }}>状态</th>
                  <SortHeader sortKey="calls" currentSort={sort.key} currentDir={sort.dir} onSort={onSort} align="right">收益记录</SortHeader>
                  <SortHeader sortKey="totalSpent" currentSort={sort.key} currentDir={sort.dir} onSort={onSort} align="right">毛收入</SortHeader>
                  <SortHeader sortKey="firstSeen" currentSort={sort.key} currentDir={sort.dir} onSort={onSort}>首次</SortHeader>
                  <SortHeader sortKey="lastSeen" currentSort={sort.key} currentDir={sort.dir} onSort={onSort}>最近</SortHeader>
                  <th style={{ padding: '6px 8px' }}>截至所选末日 7 天</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => (
                  <tr
                    key={c.callerRef}
                    data-caller-ref={c.callerRef}
                    style={{ borderTop: '1px solid var(--bd-1)', cursor: 'pointer' }}
                    onClick={() => navigate('workspace-caller', { apiId, callerId: c.callerRef })}
                    title="点击进入该 caller 的 drill-down"
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-1)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ padding: '6px 8px' }} className="mono" title={c.callerHandle}>
                      {c.callerHandle}
                    </td>
                    <td style={{ padding: '6px 8px' }}><StatusPill status={c.status} /></td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtNum(c.earningRecords ?? c.calls)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtUsd(c.totalSpent)}</td>
                    <td style={{ padding: '6px 8px', color: 'var(--fg-3)' }}>{fmtRelDate(c.firstSeen)}</td>
                    <td style={{ padding: '6px 8px', color: 'var(--fg-3)' }}>{fmtRelDate(c.lastSeen)}</td>
                    <td style={{ padding: '6px 8px' }}><Sparkline values={c.sparkline || []} /></td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
            <div aria-label="调用方分页" style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 12 }}>
              <button className="btn btn-sm btn-ghost" disabled={!currentPage.previous.length || loading} onClick={() => {
                const previous = [...currentPage.previous]; const previousCursor = previous.pop();
                setPageState({ key: queryKey, cursor: previousCursor, previous });
              }}>上一页</button>
              <span>第 {currentPage.previous.length + 1} 页 · 共 {fmtNum(insights?.totalFiltered ?? 0)} 个匹配调用方</span>
              <button className="btn btn-sm btn-ghost" disabled={!hasMore || loading} onClick={() => setPageState({ key: queryKey, cursor: insights.nextCursor, previous: [...currentPage.previous, cursor] })}>下一页</button>
            </div>
          </>
        )}
      </Section>
    </div>
  );
};

const StatusPill = ({ status }) => (
  <span
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 8px', borderRadius: 999,
      background: STATUS_COLORS[status] + '22',
      color: STATUS_COLORS[status],
      fontSize: 10, fontWeight: 500,
    }}
  >
    <span style={{
      display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
      background: STATUS_COLORS[status],
    }} />
    {STATUS_LABELS[status] || status}
  </span>
);
